import { useCallback, useMemo, useState } from 'react';

import { WithTooltip } from '@r1-frontend/ui-react/experimental/tooltip';
import { TTooltipProps } from '@r1-frontend/ui-react/experimental/tooltip/types';

import { Advertiser, IAdvertiserProps } from './AdvertiserInfo';
import { AdvertisingLabel, IAdvertisingLabelProps } from './AdvertisingLabel';

export interface IAdvertisingLabelWithTooltipProps extends IAdvertisingLabelProps, IAdvertiserProps {
    tooltip?: Omit<
        TTooltipProps,
        'children' | 'content' | 'closeOnScroll'
    >,
}

/**
 * Рекламный лейбл с поддержкой отображения тултипа по клику на лейбл
 * @example
 * <AdvertisingLabelWithTooltip
 *     companyName={companyName}
 *     erid={erid}
 *     tooltip={{ zIndex: 999 }}
 * />
 */
export const AdvertisingLabelWithTooltip = ({
    tooltip = {},
    onClick,
    companyName,
    erid,
    children = 'Реклама',
    ...props
}: IAdvertisingLabelWithTooltipProps) => {
    const [isShown, setIsShown] = useState(false);

    const onLabelClick: React.MouseEventHandler<HTMLDivElement> = useCallback((e) => {
        if (tooltip.isShown === undefined) {
            setIsShown(!isShown);
        }

        if (typeof onClick === 'function') {
            onClick(e);
        }
    }, [isShown, onClick, tooltip.isShown]);

    const isTooltipShown = useMemo(
        () => tooltip.isShown !== undefined ? tooltip.isShown : isShown,
        [tooltip.isShown, isShown],
    );

    const onTooltipClose = useCallback(() => {
        if (tooltip.isShown === undefined) {
            return setIsShown(false);
        }

        const { onClose } = tooltip;

        if (typeof onClose === 'function') {
            onClose();
        }
    }, [tooltip]);

    return (
        <WithTooltip
            isShown={isTooltipShown}
            onClose={onTooltipClose}
            content={
                <Advertiser
                    companyName={companyName}
                    erid={erid}
                />
            }
            closeOnScroll
            {...tooltip}
        >
            <AdvertisingLabel onClick={onLabelClick} {...props}>
                {children}
            </AdvertisingLabel>
        </WithTooltip>
    );
};
